import * as angular from "angular";
import { Street } from "../models/street.model";
import { CheckManager } from "../shared/check-manager";
import { CampaignDataProvider } from "./campaign.dp";
import { CanvassStatusDataProvider } from "./canvass-status.dp";
import { HouseService } from "../services/house.service";
import { Campaign } from "../models/campaign.model";
import { Effort } from "../models/effort.model";
import { Organisation } from "../models/organisation.model";
import { Filter } from "../models/filter.model";

export class StreetDataProvider {
    private $q: any;
    private $http: any;
    private __env: any;
    private campaignDataProvider: CampaignDataProvider;
    public checkManager: CheckManager;
    private canvassStatusDataProvider: CanvassStatusDataProvider;
    private houseService: HouseService;
    private localStorageService: any;

    public streets = [];
    public searchPromise: any;
    public filters = new Filter();
    public effort: Effort;
    public scrollLoading = false;
    public loading = true;
    public error = false;
    public localStorage: any;
    public activeCampaign: Campaign;
    public isLeaderView: boolean;
    public countError: boolean;
    public defaultColumns = [
        {
            id: "haddress",
            name: "Name",
            selected: true,
            type: null,
            disabled: true,
        },
        { id: "hcity", name: "City", selected: true, type: null },
        { id: "hstate", name: "State", selected: true, type: null },
        {
            id: "hprecinct",
            name: "Precinct",
            selected: true,
            type: null,
        },
        {
            id: "hzipcode",
            name: "Zip Code",
            selected: true,
            type: null,
        },
    ];
    public count?: number;
    public columns: any;

    constructor(
        $q: any,
        $http: any,
        __env: any,
        CampaignDataProvider: CampaignDataProvider,
        CanvassStatusDataProvider: CanvassStatusDataProvider,
        HouseService: HouseService,
        localStorageService: any
    ) {
        this.$q = $q;
        this.$http = $http;
        this.__env = __env;
        this.campaignDataProvider = CampaignDataProvider;
        this.checkManager = new CheckManager(this.streets);
        this.canvassStatusDataProvider = CanvassStatusDataProvider;
        this.houseService = HouseService;
        this.localStorageService = localStorageService;
        this.activeCampaign = this.campaignDataProvider.getActiveCampaign();
    }

    setColumns(): void {
        this.columns =
            this.localStorage && this.localStorage.streetColumns
                ? this.localStorage.streetColumns
                : this.defaultColumns;

        angular.forEach(this.defaultColumns, (defaultColumn: any) => {
            let found = false;
            angular.forEach(this.columns, (column: any) => {
                if (defaultColumn.id == column.id) {
                    found = true;
                }
            });
            if (!found) {
                this.columns.push(defaultColumn);
            }
        });
    }

    getColumns(): Promise<any> {
        const deferred = this.$q.defer();
        if (this.isLeaderView) {
            this.campaignDataProvider
                .getOrganisations()
                .then((organisation: Organisation) => {
                    this.localStorage = organisation.getLocalStorage();
                    this.setColumns();
                    this.addStatusesToColumns();
                    deferred.resolve(this.columns);
                });
        } else {
            this.localStorage = this.activeCampaign.getLocalStorage();
            this.setColumns();
            this.addStatusesToColumns();
            deferred.resolve(this.columns);
        }

        return deferred.promise;
    }

    addStatusesToColumns(): void {
        this.canvassStatusDataProvider
            .walkCanvassStatuses()
            .then((statuses: Array<any>) => {
                angular.forEach(statuses, (status) => {
                    let found = false;
                    angular.forEach(this.columns, (column) => {
                        if (status.id == column.id) {
                            found = true;
                        }
                    });
                    if (!found)
                        this.columns.push({
                            id: status.id,
                            name: status.name,
                            selected: true,
                            color: status.color,
                            type: "status",
                        });
                });
            });
    }

    clearAll(): void {
        this.filters = new Filter();
        this.searchPromise = null;
        this.streets.splice(0, this.streets.length);
        this.count = undefined;
    }

    setFilters(setFilters: Filter): void {
        this.filters = setFilters;
        this.filters.removeSelectedUsers();
        this.resetItems();
    }

    setEffort(newEffort: Effort): void {
        if (newEffort !== this.effort) {
            this.effort = newEffort;
            this.resetItems();
        }
    }

    getFilteredStreets(lastStreet: any, scroll: boolean): Promise<any> {
        if (
            this.effort &&
            Object.keys(this.filters).length &&
            (!angular.isDefined(this.count) || this.streets.length < this.count)
        ) {
            this.error = false;

            if (scroll) this.scrollLoading = true;
            else this.loading = true;
            let returnPromise = this.$q.defer();

            if (this.searchPromise) {
                this.searchPromise.resolve("user cancelled");
            }
            this.searchPromise = this.$q.defer();

            let last = null;
            if (lastStreet) {
                last = {
                    street: lastStreet,
                    relevance: lastStreet.relevance,
                };
            }

            this.$http({
                url: this.__env.apiUrl() + "/house/streets",
                method: "GET",
                params: {
                    last: last,
                    filters: this.filters.nonEmptyValues(),
                    effort: this.effort,
                    count: this.count,
                },
                timeout: this.searchPromise.promise,
            }).then(
                (response: object) => {
                    const json = response["data"];
                    if (!last) {
                        this.streets.splice(0, this.streets.length);
                    }

                    if (json.success) {
                        if (!scroll && !this.isLeaderView) {
                            this.getCount();
                        }

                        for (let i = 0; i < json.data.length; i++) {
                            let street = new Street(json.data[i]);
                            this.checkManager.checkNewItem(street);
                            this.streets.push(street);
                        }
                        returnPromise.resolve(this.streets);
                    } else {
                        returnPromise.reject(response);
                    }
                    if (scroll) this.scrollLoading = false;
                    else this.loading = false;
                },
                (error: object) => {
                    if (error["status"] > -1) {
                        this.error = true;

                        if (scroll) this.scrollLoading = false;
                        else this.loading = false;
                    }
                    returnPromise.reject(error);
                }
            );
            return returnPromise.promise;
        }
    }

    getCount(): Promise<any> {
        this.countError = false;
        const deferred = this.$q.defer();
        let activeFilters = this.filters.nonEmptyValues();

        this.$http({
            url: "/api/house/streetsCount",
            method: "GET",
            params: {
                filters: JSON.stringify(activeFilters),
                effort: this.effort,
            },
            timeout: this.searchPromise.promise,
        }).then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    this.setFilteredStreetsCount(parseInt(json.data));
                    deferred.resolve(parseInt(json.data));
                }
            },
            (error: object) => {
                if (error["status"] > -1) {
                    this.countError = true;
                }
                deferred.reject(error);
            }
        );
        return deferred.promise;
    }

    getFilteredStreetsCount(): number {
        return this.count;
    }

    setFilteredStreetsCount(streetsCount: number): void {
        this.count = streetsCount;
        this.checkManager.setTotalCount(this.count);
    }

    resetItems(): void {
        this.setFilteredStreetsCount(undefined);
        this.getFilteredStreets(null, false);
    }

    bulkDelete(): Promise<any> {
        const deferred = this.$q.defer();
        this.houseService
            .bulk_delete_streets(this.checkManager.bulkActionData(this.filters))
            .then(
                (response: object) => {
                    const json = response["data"];
                    if (json.success) {
                        this.resetItems();
                        this.checkManager.removeCheckedItems();
                        deferred.resolve(json.data);
                    } else {
                        deferred.reject(json);
                    }
                },
                (response: object) => deferred.reject(response)
            );
        return deferred.promise;
    }
}

angular
    .module("vcDataProvider")
    .service("StreetDataProvider", StreetDataProvider);
